import { Box, CircularProgress, Icon, Typography, useMediaQuery, useTheme } from '@mui/material'
import AdxSwitch from 'components/reusable/switch/AdxSwitch'
import React, { useEffect, useState } from 'react'
import PersonalityInfoModal from './PersonalityInfoModal'
import RadarChart from './RadarChart'
import BehavioralScore from './BehavioralScore'
import { getPersonalityData } from 'services/api/modules/recruitment/job-application'
import { useQuery, useQueryClient } from 'react-query'
import EvaluateTeamFit from './EvaluateTeamFit'

const PersonalityCard = ({ id }) => {
  const [switchState, setSwitchState] = useState(false)
  const [evaluateTeamData, setEvaluateTeamData] = useState(null)

  // API CALL
  const queryClient = useQueryClient()

  const { isLoading, error, data } = useQuery(
    ['getPersonalityData', id],
    async () => {
      const { data } = await getPersonalityData(id)
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  useEffect(() => {
    queryClient.invalidateQueries('getPersonalityData')
  }, [data])

  const personalityData = data?.personality_traits

  const handleResetData = () => {
    setUserData({
      labels: ['FREEDOM (%)', 'ADVENTUROUS (%)', 'TEAMWORK (%)', 'SYSTEMATIC (%)', 'AMBITIOUS (%)'],
      datasets: [
        {
          data: [
            personalityData?.freedom?.dominant_percentage,
            personalityData?.adventurous?.dominant_percentage,
            personalityData?.teamwork?.dominant_percentage,
            personalityData?.systematic?.dominant_percentage,
            personalityData?.ambitious?.dominant_percentage,
          ],
          backgroundColor: 'rgba(245, 210, 22, 0.2)',
          borderColor: 'rgb(208, 154, 1)',
          borderWidth: 4,
        },
      ],
    })
  }

  const handleTeamData = (data) => {
    handleResetData()
    setEvaluateTeamData(data)
    updateUserData(data)
  }

  const [userData, setUserData] = useState({
    labels: ['FREEDOM (%)', 'ADVENTUROUS (%)', 'TEAMWORK (%)', 'SYSTEMATIC (%)', 'AMBITIOUS (%)'],
    datasets: [
      {
        data: [
          personalityData?.freedom?.dominant_percentage,
          personalityData?.adventurous?.dominant_percentage,
          personalityData?.teamwork?.dominant_percentage,
          personalityData?.systematic?.dominant_percentage,
          personalityData?.ambitious?.dominant_percentage,
        ],
        backgroundColor: 'rgba(245, 210, 22, 0.2)',
        borderColor: 'rgb(208, 154, 1)',
        borderWidth: 4,
      },
    ],
  })

  const submittedAt = new Date(data?.submitted_at)
  const year = submittedAt.getFullYear().toString().slice(-2)
  const month = (submittedAt.getMonth() + 1).toString().padStart(2, '0')
  const day = submittedAt.getDate().toString().padStart(2, '0')
  const formattedDate = `${day}/${month}/${year}`

  const updateUserData = (teamData) => {
    if (teamData) {
      setUserData((prevData) => ({
        ...prevData,
        datasets: [
          ...prevData.datasets,
          {
            data: [
              teamData?.department?.personality_traits?.freedom?.average_percentage,
              teamData?.department?.personality_traits?.adventurous?.average_percentage,
              teamData?.department?.personality_traits?.teamwork?.average_percentage,
              teamData?.department?.personality_traits?.systematic?.average_percentage,
              teamData?.department?.personality_traits?.ambitious?.average_percentage,
            ],
            borderColor: '#2666BE',
            backgroundColor: '#DEECFF75',
            borderWidth: 4,
          },
          {
            data: [
              teamData?.employee?.personality_traits?.freedom?.dominant_percentage,
              teamData?.employee?.personality_traits?.adventurous?.dominant_percentage,
              teamData?.employee?.personality_traits?.teamwork?.dominant_percentage,
              teamData?.employee?.personality_traits?.systematic?.dominant_percentage,
              teamData?.employee?.personality_traits?.ambitious?.dominant_percentage,
            ],
            borderColor: '#2666BE',
            backgroundColor: '#DEECFF75',
            borderWidth: 4,
            borderDash: [10, 10],
          },
        ],
      }))
    }
  }

  useEffect(() => {
    setUserData({
      ...userData,
      datasets: [
        {
          ...userData.datasets[0],
          data: [
            personalityData?.freedom?.dominant_percentage,
            personalityData?.adventurous?.dominant_percentage,
            personalityData?.teamwork?.dominant_percentage,
            personalityData?.systematic?.dominant_percentage,
            personalityData?.ambitious?.dominant_percentage,
          ],
        },
      ],
    })
  }, [personalityData, id])

  return (
    <Box
      sx={{
        padding: '24px 24px 24px 32px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '24px',
      }}
    >
      {data ? (
        <>
          <Box
            border={1}
            borderColor="gray-line.main"
            borderRadius="4.5px"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '16px',
              }}
              padding="16px"
              width="342px"
              borderRight={1}
              borderColor="gray-line.main"
            >
              <Icon sx={{ color: 'alternate.main' }}>assignment</Icon>
              <Typography width="100%">
                Submitted on <span style={{ fontWeight: 600 }}>{formattedDate}</span>
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <AdxSwitch
                state={switchState}
                onChange={() => {
                  setSwitchState(!switchState)
                  handleResetData()
                  setEvaluateTeamData(null)
                }}
              />
              <Typography paddingRight="16px" variant="body1" sx={{ width: '140px' }}>
                Evaluate Team Fit
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
              padding="16px"
              width="55px"
              borderLeft={1}
              borderColor="gray-line.main"
            >
              <PersonalityInfoModal variant="card" />
            </Box>
          </Box>
        </>
      ) : (
        <>
          <EmptyState />
        </>
      )}
      <Box
        sx={{
          minWidth: '100%',
          minHeight: '100%',
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              width: '100%',
              height: 200,
            }}
          >
            <CircularProgress color="alternate" />
          </Box>
        ) : (
          <>
            <RadarChart chartData={userData} />
            <RenderScoreBoxes
              personalityData={personalityData}
              evaluateTeamData={evaluateTeamData}
              switchState={switchState}
            />
          </>
        )}
      </Box>
      {data &&
        (switchState ? (
          <EvaluateTeamFit evaluateTeamData={handleTeamData} userScore={personalityData} />
        ) : (
          <BehavioralScore data={data} />
        ))}
    </Box>
  )
}

export default PersonalityCard

export const RenderScoreBoxes = ({ personalityData, evaluateTeamData, switchState }) => {
  // const theme = useTheme()
  // const sm = useMediaQuery(theme.breakpoints.up('sm'))
  // const mobile_l = useMediaQuery(theme.breakpoints.up('mobile_l'))

  const scoreData = [
    { label: 'freedom', right: '50%', top: '23%' },
    { label: 'adventurous', right: '9%', top: '45%' },
    { label: 'teamwork', right: '23%', top: '83%' },
    { label: 'systematic', right: '79%', top: '83%' },
    { label: 'ambitious', right: '90%', top: '45%' },
  ]

  const scoreDataExpanded = [
    { label: 'freedom', right: '44%', top: '23%' },
    { label: 'adventurous', right: '3%', top: '45%' },
    { label: 'teamwork', right: '17%', top: '83%' },
    { label: 'systematic', right: '72%', top: '83%' },
    { label: 'ambitious', right: '83%', top: '45%' },
  ]

  if (!switchState) {
    return (
      <>
        {scoreData?.map((data) => (
          <Box
            key={data?.label}
            sx={{
              position: 'absolute',
              backgroundColor: personalityData ? 'alternate.main' : 'gray-background.main',
              color: personalityData ? 'text.white' : 'text-secondary',

              padding: '6px',
              right: data.right,
              top: data.top,
              borderRadius: '3.5px',
            }}
          >
            {(personalityData &&
              personalityData[data.label] &&
              personalityData[data.label]?.dominant_percentage) ??
              '--'}
          </Box>
        ))}
      </>
    )
  } else {
    return (
      <>
        {scoreDataExpanded?.map((data) => (
          <Box
            key={data.label}
            sx={{
              position: 'absolute',
              right: data?.right,
              top: data?.top,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'primary.main',
                color: 'text.primary',
                padding: '6px',
                borderRadius: '3.5px',
                width: '35px',
                height: '35px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {personalityData &&
                personalityData[data.label] &&
                personalityData[data.label]?.dominant_percentage}
            </Box>
            <Box
              sx={{
                backgroundColor: evaluateTeamData?.department
                  ? 'alternate.main'
                  : 'gray-background.main',
                color: evaluateTeamData?.department ? 'text.white' : 'light-gray-text-2.main',
                padding: '6px',
                borderRadius: '3.5px',
                width: '35px',
                height: '35px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {(evaluateTeamData &&
                evaluateTeamData?.department?.personality_traits[data.label] &&
                evaluateTeamData?.department?.personality_traits[data.label]?.average_percentage) ??
                '--'}
            </Box>
            <Box
              sx={{
                backgroundColor: evaluateTeamData?.employee
                  ? 'alternate.light'
                  : 'gray-background.main',
                color: evaluateTeamData?.employee ? 'text.primary' : 'text.secondary',
                border: evaluateTeamData?.employee ? '2px dashed' : null,
                borderColor: evaluateTeamData?.employee ? 'alternate.main' : 'grayscale.line-light',
                padding: '6px',
                borderRadius: '3.5px',
                width: '35px',
                height: '35px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {(evaluateTeamData &&
                evaluateTeamData?.employee?.personality_traits[data.label] &&
                evaluateTeamData?.employee?.personality_traits[data.label]?.dominant_percentage) ??
                '--'}
            </Box>
          </Box>
        ))}
      </>
    )
  }
}

export const EmptyState = () => {
  return (
    <Box
      border={1}
      borderColor="gray-line.main"
      borderRadius="4.5px"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '16px',
        }}
        padding="16px"
        width="100%"
        borderRight={1}
        borderColor="gray-line.main"
      >
        <Icon sx={{ color: 'warning.main' }}>error</Icon>
        <Typography width="100%">This candidate has not taken the personality test.</Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        padding="16px"
        width="55px"
      >
        <PersonalityInfoModal variant="card" />
      </Box>
    </Box>
  )
}
